import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Adds hover elevation and a pointer. For cards that navigate or drag. */
  interactive?: boolean;
  /** Removes internal padding when the card wraps a table or its own layout. */
  flush?: boolean;
  children: ReactNode;
}

/**
 * Elevation, not outline. The card sits one surface step above the canvas
 * and catches a hairline of light on its top edge; the border is there only
 * to hold the shape where two surfaces meet at similar lightness.
 */
export function Card({ interactive = false, flush = false, children, ...props }: CardProps) {
  return (
    <div
      className={cx(
        'surface-lift rounded-lg border border-border bg-surface',
        !flush && 'p-4',
        interactive &&
          'cursor-pointer transition hover:border-border-strong hover:bg-surface-raised',
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned control, usually a ghost or secondary Button. */
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="text-sm font-medium text-fg">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-fg-subtle">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** Full-bleed divider inside a padded card. */
export function CardDivider() {
  return <div className="-mx-4 my-4 h-px bg-border-subtle" />;
}
